import lodash from 'lodash';
import { OrderingConstants, OrderingOperations, OrderingSelectors } from 'polygon-ordering';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiArrowDownSLine } from 'react-icons/ri';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import ChoiceSet from '../components/ChoiceSet';
import ItemTopHeader from '../components/ItemTopHeader';
import Modal from '../components/Modal';
import NestedChoiceSetBanner from '../components/NestedChoiceSetBanner';
import NestedChoiceSets from '../components/NestedChoiceSets';
import {
  createPurchaseFromStages,
  INestedChoiceSetsContext,
  NestedChoiceSetsContext,
  NestedIngredientsStages,
} from '../components/NestedChoiceSetsContext';
import QuantityControl from '../components/QuantityControl';
import RedcatImage from '../components/RedcatImage';
import StandardButton from '../components/StandardButton';
import Text from '../components/Text';
import TouchableOpacity from '../components/TouchableOpacity';
import { DEVICE_TYPES } from '../constants';
import { computeTagsForItem } from '../libs/polygon-ordering/src/utils/ordering/computeTagsForItem';
import getThemeLookup from '../selectors/getThemeLookup';
import { setCurrentModal } from '../thunks/setCurrentModal';
import combineStyles from '../utils/combineStyles';
import { CONTAINER_PROPERTIES } from '../utils/theme';
import MessageModal from './MessageModal';
import { MiamSection } from './MIAM';
import { UPSELL_MODAL } from './Upsells';
import AddToCartButton from '../components/AddToCartButton';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import { addToCart, updateCart } from '../utils/analytics';
import getCart from '../libs/polygon-ordering/src/selectors/getCart';
import { itemContainsAlcoholicItems } from '../libs/polygon-ordering/src/utils/ordering/filterAlcoholic';

const {
  getOpenPurchase,
  getOpenPurchasePreviouslyStaged,
  getHideNegativeIngredients,
  getOpenPurchaseSizes,
  getAllergenDietaryDetails,
  getNestedItemStockBalancesData,
  getUpsellsByItem,
  getEnabledUpsellByItem,
  getUpsellDefaultTitle,
  getItemAvailability,
  getMenu,
  getBrands,
  getMember,
  getSelectedOffer,
  getLocation,
  getUserUnderage,
  getSaleType,
} = OrderingSelectors;
const { OPEN_PURCHASE_LOWEST_BALANCE_DEFAULT } = OrderingConstants;
const {
  stageOpenPurchase,
  changeOpenPurchaseItem,
  adjustOpenPurchaseChoice,
  setOpenPurchaseLowestBalance,
  adjustOpenPurchaseQuantity,
  clearOpenPurchase,
} = OrderingOperations;

export const PURCHASE_EDITOR_MODAL_ID = 'PURCHASE_EDITOR_MODAL_ID';

const addToCartLogging = ({
  category,
  cart,
  item,
  member,
  quantity,
  choicesWithQuantity,
  brand,
  selectedOffer,
  menu,
  location,
  saleType,
}: {
  category?: Category;
  cart: CartItem[];
  item: Item;
  member?: Member;
  quantity: number;
  choicesWithQuantity: ChoiceWithQuantity[];
  brand: string | null;
  selectedOffer?: Offer;
  menu?: Menu;
  location?: POSLocation | null;
  saleType: SALE_TYPE;
}) => {
  addToCart({
    product: item,
    moneyPrice: item.baseMoneyPrice,
    quantity,
    choicesWithQuantity,
    brand,
    category: category?.name || null,
    member,
    store: location?.name,
    saleType,
    origin: 'Product Page',
  });

  /**
   * adjustStagedPurchaseQuantity cannot be awaited, but we need to log the updated
   * cart value to updateCart. Will need to check if the item already exists in the
   * cart and update the quantity, and if it doesn't exist it needs to be added.
   */
  const itemAlreadyInCart = cart.find(cartItem => {
    const productId = item.default_size || item.id;
    if (cartItem.ProductID.toString() !== productId) return false;
    if (cartItem.Ingredients.length !== choicesWithQuantity.length) return false;

    const map = new Map();
    for (const { ProductID, Qty } of cartItem.Ingredients) {
      map.set(ProductID, (map.get(ProductID) || 0) + Qty);
    }

    for (const { id, quantity } of choicesWithQuantity) {
      if (map.get(Number(id)) !== quantity) return false;
      map.delete(Number(id));
    }

    return map.size === 0;
  });
  if (itemAlreadyInCart) {
    itemAlreadyInCart.Qty = itemAlreadyInCart.Qty + quantity;
  } else if (cart) {
    cart = [
      ...cart,
      {
        Ingredients: choicesWithQuantity.map(cwq => ({
          Price: cwq.baseMoneyPrice,
          ProductID: Number(cwq.id),
          Qty: cwq.quantity,
          PLUCode: cwq.plucode ? Number(cwq.plucode) : undefined,
        })),
        Price: item.baseMoneyPrice,
        ProductID: item.default_size ? Number(item.default_size) : Number(item.id),
        Qty: quantity,
        PLUCode: item.plucode ? Number(item.plucode) : undefined,
      } as CartItem,
    ];
  }
  updateCart({ member, cart, selectedOffer, menu });
};

const BOTTOM_PADDING = 170;

const PurchaseEditorModal = () => {
  const openPurchase = useAppSelector(getOpenPurchase);
  const sizes = useAppSelector(getOpenPurchaseSizes);

  const p = useAppSelector(getThemeLookup);
  const previouslyStaged = useAppSelector(getOpenPurchasePreviouslyStaged);
  const [pages, setPages] = useState<(ValidatedChoiceSet | ValidatedNestedChoiceSet)[][]>([]);
  const [activatePage, setActivatePage] = useState(0);

  const hideNegativeIngredients = useAppSelector(getHideNegativeIngredients);
  const collapsableChoiceSetsEnabled = useAppSelector(
    state => state.config.collapsableChoiceSetsEnabled,
  );
  const collapseOptionalChoiceSetsByDefault = useAppSelector(
    state => state.config.collapseOptionalChoiceSetsByDefault,
  );
  const brands = useAppSelector(getBrands);
  const member = useAppSelector(getMember);
  let cart = useAppSelector(getCart);

  const [state, setState] = useState<{
    showIndicator: boolean;
    collapsedChoiceSets: { [key: string]: boolean };
  }>({
    showIndicator: false,
    collapsedChoiceSets: {},
  });
  const psbContainerRef = useRef<any>(null);
  const { t } = useTranslation();
  const {
    purchased,
    setPurchased,
    stages,
    dataForAdjust,
    setDataForAdjust,
    setStages,
    setShowChoices,
    showNestedItem,
    setShowNestedItem,
    nestedItemInValid,
    nestedChoiceSetsTotal,
    isSelectionValid,
    miamChoiceSet,
  } = useContext(NestedChoiceSetsContext) as INestedChoiceSetsContext;

  const steps = Object.keys(stages || {}).length;
  const [messageBox, setMessageBox] = useState(false);

  const menu = useAppSelector(getMenu);
  const allChoiceSets = menu?.choiceSets as ChoiceSets | NestedChoiceSets;

  const enableAddItemButton = (
    stages: NestedIngredientsStages,
    choiceSets: SDict<ChoiceSet | NestedChoiceSet>,
  ): boolean => {
    return (
      (steps === 3 &&
        choiceSets &&
        'nestedIngredients' in choiceSets[(stages || {})[0] as string]) ||
      (steps === 2 &&
        choiceSets &&
        !('nestedIngredients' in choiceSets[(stages || {})[0] as string]))
    );
  };

  useEffect(() => {
    // assess();
    dispatch(setOpenPurchaseLowestBalance(OPEN_PURCHASE_LOWEST_BALANCE_DEFAULT));
  });

  const deviceType = useAppSelector(state => state.deviceType);

  useEffect(() => {
    const choiceSets = lodash.get(openPurchase, 'choiceSets', []);
    const collapsedChoiceSets: { [key: string]: boolean } = {};

    choiceSets.forEach((choiceSet: ValidatedChoiceSet) => {
      const isRadioButton = choiceSet.min === 1 && choiceSet.max === 1;
      if (!choiceSet.min || (choiceSet.valid && !isRadioButton)) {
        collapsedChoiceSets[choiceSet.id] = collapsableChoiceSetsEnabled
          ? collapseOptionalChoiceSetsByDefault
          : false;
      }
    });

    setState({ ...state, collapsedChoiceSets });
  }, [collapsableChoiceSetsEnabled, collapseOptionalChoiceSetsByDefault, openPurchase?.item.id]);

  const dispatch = useAppDispatch();
  const category = useAppSelector(state => state.modalParams?.category);
  const {
    item,
    choiceSets = [],
    moneyPrice,
    valid,
    quantity,
    sizeId,
    choicesWithQuantity,
    brandId,
  } = openPurchase || {};

  const [daypartAvailability, availabilityString] = useAppSelector(
    getItemAvailability((item || {}) as Item, category?.id || '', true),
  );
  const upsells = useAppSelector(getUpsellsByItem(item));
  const enableUpsell = useAppSelector(getEnabledUpsellByItem);
  const upsellDefaultTitle = useAppSelector(getUpsellDefaultTitle);
  const upsellTitle = item?.upsell_title ? item?.upsell_title : upsellDefaultTitle;
  const selectedOffer = useAppSelector(getSelectedOffer);
  const location = useAppSelector(getLocation);
  const [, minBalanceStaged] = useAppSelector(
    getNestedItemStockBalancesData(openPurchase, purchased),
  );
  const [modified, setModified] = useState<boolean>(false);
  // use this state variable when we add actual PLU images
  // TODO: make this not a placeholder, or fix logic to be  more elegant, I just added these to make my mobile modal fixes more maintainable
  const [imageShowing, setImageShowing] = useState<string | undefined>();
  // set this once to the default image of the composite item object
  // when we change items, don't change the image from this (unless going to a nested item)
  const [compositeImage, setCompositeImage] = useState<string | undefined>();
  const isUserUnderage = useAppSelector(getUserUnderage);
  const saleType = useAppSelector(getSaleType);

  //store the scroll position for nested choicesets
  const [scrollTop, setScrollTop] = useState(0);
  useEffect(() => {
    if (psbContainerRef.current) {
      if (!showNestedItem) psbContainerRef.current.scrollTo({ top: scrollTop });
      else {
        psbContainerRef.current.scrollTo({ top: 0 });
      }
    }
  }, [showNestedItem]);

  const isRootItem = steps === 0;

  // NOTE: past Heath's code here sucks, the useeffects have the same dependencies. TODO: at least combine these useeffects when you refactor this image logic
  // if we are the root item then set the image to the root item image (or composite if set)
  useEffect(() => {
    if (isRootItem) {
      setImageShowing(compositeImage ?? sizeItem?.images.default);
    }
  }, [item, isRootItem, compositeImage]);
  useEffect(() => {
    // composite image is only set once
    if (isRootItem && isComposite && !compositeImage) {
      // item is assumed to be root composite item in this case (as the composite image has not already been set)
      setCompositeImage(item.images.default);
    }
  }, [item, compositeImage, isRootItem]);

  useEffect(() => {
    const { choiceSets } = openPurchase || {};
    if (choiceSets)
      if (isComposite && mixinChoicesets.length > 0) {
        setPages([choiceSets.filter(c => !mixinRegex.test(c.name)), mixinChoicesets]);
      } else {
        setPages([choiceSets]);
      }
  }, [openPurchase]);

  const tagDetails = useAppSelector(getAllergenDietaryDetails);

  if (!openPurchase || !item) {
    dispatch(setCurrentModal(null));
    return null;
  }

  const brand = brands.find(brand => brand.id === brandId)?.name || null;
  const availabilityTimeString = item?.availability
    ? t('itemAvailable', { itemAvailable: availabilityString })
    : '';
  const sizeItem = (!!item?.sizes.length && item?.sizes.find(s => s.id === sizeId)) || item;
  const isComposite = !!item?.sizes.length;

  const mixinRegex = /mix-ins/i;

  const mixinChoicesets = choiceSets?.filter(c => mixinRegex.test(c.name));

  const morePagesExist = activatePage + 1 < pages.length;

  const handleBackButtonClick = async () => {
    setDataForAdjust([]);

    const steps = Object.keys(stages || {}).length;

    if (stages) {
      const newStages = { ...stages };
      delete newStages[steps - 1];
      setStages({ ...newStages });
    }
    if (steps === 3) {
      setShowChoices(false);
      setShowNestedItem(true);
    }

    if (steps === 2 || steps === 1) {
      setShowNestedItem(false);
      setShowChoices(false);
      setStages(undefined);
    }
  };

  const handleAddToItem = async () => {
    for (const data of dataForAdjust) {
      await dispatch(adjustOpenPurchaseChoice(data));
    }

    if (stages) {
      const purchase = createPurchaseFromStages(stages);
      setPurchased({ ...purchased, ...purchase });
    }

    setDataForAdjust([]);

    setShowChoices(false);
    setShowNestedItem(false);
    setStages(undefined);
  };

  const toggleCollapsed = (id: string) => {
    const { collapsedChoiceSets } = state;

    setState({
      ...state,
      collapsedChoiceSets: {
        ...collapsedChoiceSets,
        [id]: !collapsedChoiceSets[id],
      },
    });
  };

  const handleAddToCart = async () => {
    if (enableUpsell && !previouslyStaged)
      dispatch(
        setCurrentModal({
          modalId: UPSELL_MODAL,
          params: {
            items: upsells,
            title: upsellTitle || t('title.modal.upsellTitle'),
          },
        }),
      );
    // analytics
    // TODO: correct price here?
    // actual cart stuff
    const stagedItem = await dispatch(
      stageOpenPurchase(
        choiceSets.filter(
          c =>
            'nestedIngredients' in c &&
            c.displayType !== 'checkbox' &&
            c.displayType !== 'makeameal',
        ).length >= 1
          ? { choiceSelections: purchased, enableUpsell }
          : { enableUpsell },
      ),
    );
    addToCartLogging({
      category,
      cart,
      item: item.sizes.find(s => s.id === stagedItem.payload.purchase.sizeId) || item,
      member,
      quantity: quantity || 0,
      choicesWithQuantity: choicesWithQuantity || [],
      brand,
      selectedOffer,
      menu,
      location,
      saleType,
    });
  };
  const nested = !!choiceSets.filter(c => 'nestedIngredients' in c && c.displayType !== 'checkbox')
    .length;

  const validateCart = () => {
    return nested ? nestedItemInValid : !valid;
    // ||
    // (minBalanceStaged !== undefined && quantity > minBalanceStaged)
  };

  const { showIndicator, collapsedChoiceSets } = state;

  const SIZE_CHOICE_SET = {
    id: 'SIZE_CHOICE_SET',
    name: item.sizes_label || t('title.sizeChoice'),
    free: 0,
    max: 1,
    min: 1,
    valid: true,
    quantity: 1,
    choices: (sizes || []).map((size: { [key: string]: any }) => ({
      ...size,
      name: size.name,
      quantity: openPurchase.sizeId === size.id ? 1 : 0,
      baseMoneyPrice: size.baseMoneyPrice - item.minSizeBaseMoneyPrice,
    })),
  };

  SIZE_CHOICE_SET.choices = SIZE_CHOICE_SET.choices.filter((choice: any) => {
    const itemChoice = item.sizes.find(size => size.id === choice.id);
    return itemChoice && (!isUserUnderage || !itemContainsAlcoholicItems(itemChoice));
  });

  const noNestedChoiceSets = !(
    choiceSets.filter(
      c =>
        'nestedIngredients' in c && c.displayType !== 'checkbox' && c.displayType !== 'makeameal',
    ).length > 0
  );

  const handleClose = () => {
    // only show the close warning if the user changed anything
    if (!modified) {
      // dispatch(resetTempTags());
      dispatch(setCurrentModal(null));
      dispatch(clearOpenPurchase());
    } else {
      setMessageBox(true);
    }
  };

  const isMobileDevice = deviceType == DEVICE_TYPES.MOBILE;

  const baseContainerStyle = combineStyles(styles.container, { padding: isMobileDevice ? 10 : 30 });

  const normalChoiceSetContainerStyle = combineStyles(
    baseContainerStyle,
    // p('defaultBorder', ['borderTop']),
    isMobileDevice ? { paddingTop: 20, paddingBottom: 20 } : {},
  );

  const topSectionContainerStyle = combineStyles(
    baseContainerStyle,
    isMobileDevice ? { paddingBottom: 20 } : {},
  );

  const addToItemEnabled = enableAddItemButton(stages, allChoiceSets);

  const tags = computeTagsForItem(
    sizeItem,
    (nested ? purchased : openPurchase.choiceSelections) as NestedChoiceSelections,
    tagDetails,
  );

  const handleIncreaseQty = () => {
    // if (minNestedStockBalanceStaged && quantity >= minNestedStockBalanceStaged)
    //   enqueueWarningSnackbar(t('stagedChoiceSetItemQuantityWarningMessage'));
    setModified(true);
    dispatch(adjustOpenPurchaseQuantity(1));
  };

  // TODO: refactor this image logic
  const imagePath = imageShowing ?? item.images.default;

  const enableBackButtonForMixins = isComposite && !morePagesExist && pages.length > 1;

  //miam choiceset
  const makeamealChoiceSet = noNestedChoiceSets
    ? choiceSets.find(set => set.displayType === 'makeameal')
    : miamChoiceSet;

  const getHandleBackFunction = () => {
    if (enableBackButtonForMixins) {
      return () => setActivatePage(p => p - 1);
    }

    if (isRootItem) {
      return handleClose;
    }

    return handleBackButtonClick;
  };

  const getButtonLabel = () => {
    if (morePagesExist) {
      return t('button.purchaseEditor.next');
    }

    if (previouslyStaged) {
      return t('button.purchaseEditor.modified');
    }

    return t('button.purchaseEditor.new');
  };

  const getButtonValue = () => {
    if (noNestedChoiceSets) {
      return moneyPrice || 0;
    }

    return (quantity || 0) * ((item.baseMoneyPrice || 0) + nestedChoiceSetsTotal);
  };

  return (
    <>
      <Modal
        // TODO: fix this to be correct size from wireframes? (wireframe scale different to real screens?)
        containerStyle={styles.modalContainer}
        // desktopMaxWidth={1200}
        desktopMinWidth={600}
        desktopMaxHeight={600} //TODO this is the problem
        desktopMinHeight={600}
        backgroundClick={handleClose}
        handleClose={handleClose}
        handleBack={getHandleBackFunction()}
        mobileContainerStyle={styles.mobileContainer}
        enableBackButton={enableBackButtonForMixins ? true : !isRootItem || isMobileDevice}
        tallestPossible={isMobileDevice}
        headerContainerStyle={{
          justifyContent: 'flex-start',
        }}
      >
        {/* image on desktop */}
        <div>
          {!isMobileDevice && (
            <RedcatImage
              alt={item.name}
              imagePath={imagePath}
              containerStyle={combineStyles(
                {
                  position: 'relative',
                  maxWidth: 600,
                  maxHeight: 600,
                  display: 'flex',
                  overflow: 'hidden',
                },
                p('screen', ['backgroundColor']),
                p('purchaseEditorLeft', ['backgroundColor']),
              )}
              imageStyle={p('purchaseEditorImage', ['objectFit'])}
            />
          )}
        </div>

        {/* content container */}
        <div
          style={{
            flex: 1,
            position: 'relative',
            maxWidth: 600,

            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 0,
          }}
        >
          <div
            ref={psbContainerRef}
            style={combineStyles({
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',

              // Commenting out z-perspective parallaxing due to iOS/safari buggy UI breaks

              // perspective: '2px',
              // WebkitPerspective: '2px',
              // perspectiveOrigin: 'top',
              // transformStyle: 'preserve-3d',
              // WebkitTransformStyle: 'preserve-3d',

              flex: 1,
            } as CSSProperties)}
          >
            {/* image for mobile */}
            {isMobileDevice && (
              <RedcatImage
                alt={item.name}
                imagePath={imagePath}
                containerStyle={styles.productImage}
                imageStyle={p('purchaseEditorImage', ['objectFit'])}
              />
            )}
            <div
              style={combineStyles(
                {
                  display: 'flex',
                  flexDirection: 'column',
                  flexGrow: 1,
                },
                p('screen', ['backgroundColor']),
                p('purchaseEditorRight', ['backgroundColor']),
              )}
            >
              {/* top section */}
              {isRootItem && (
                <div style={topSectionContainerStyle}>
                  <div
                    style={
                      isMobileDevice && !imagePath
                        ? { padding: 20, marginTop: 20 }
                        : { padding: 20 }
                    }
                  >
                    <ItemTopHeader
                      item={item}
                      tags={tags}
                      availabilityTimeString={availabilityTimeString}
                    />
                  </div>
                </div>
              )}
              {/* choice set sections */}
              {/* hacky seperator between header and choices */}
              {(noNestedChoiceSets || isComposite) && (
                <div style={p('defaultBorder', ['borderTop'])} />
              )}
              {/* non-sizing/composite normal choices */}
              {noNestedChoiceSets ? (
                <div style={normalChoiceSetContainerStyle}>
                  {/* composite variety choiceset */}
                  {isComposite && activatePage === 0 && (
                    <>
                      <NestedChoiceSetBanner
                        choiceSet={SIZE_CHOICE_SET as ValidatedChoiceSet}
                        collapsed={collapsedChoiceSets[SIZE_CHOICE_SET.id]}
                        toggleCollapsed={toggleCollapsed}
                      />

                      {!collapsedChoiceSets[SIZE_CHOICE_SET.id] && (
                        <ChoiceSet
                          choiceSet={SIZE_CHOICE_SET as ValidatedChoiceSet}
                          adjustOpenPurchaseChoice={({ targetChoiceId }) => {
                            setModified(true);
                            dispatch(
                              changeOpenPurchaseItem({
                                sizeId: targetChoiceId,
                                choiceSelections: openPurchase.choiceSelections,
                              }),
                            );
                          }}
                          hideNegativeIngredients={hideNegativeIngredients}
                        />
                      )}
                    </>
                  )}
                  {pages[activatePage]?.map((choiceSet, index) => {
                    const collapsed = Boolean(collapsedChoiceSets[choiceSet.id]);

                    const isRadioButton = choiceSet.min === 1 && choiceSet.max === 1;

                    const getQuantityHint = (
                      choiceSet: ValidatedChoiceSet | ValidatedNestedChoiceSet,
                      t: any,
                    ) => {
                      if (!choiceSet.min) {
                        return t('choiceSet.quantityHint.maximum', { max: choiceSet.max });
                      }

                      if (!choiceSet.max) {
                        return '';
                      }

                      if (choiceSet.min === choiceSet.max) {
                        return t('choiceSet.quantityHint.constant', { quantity: choiceSet.min });
                      }

                      return t('choiceSet.quantityHint.range', {
                        min: choiceSet.min,
                        max: choiceSet.max,
                      });
                    };

                    const getFreeSuffix = (
                      choiceSet: ValidatedChoiceSet | ValidatedNestedChoiceSet,
                      t: any,
                    ) => {
                      const hasFreeSuffix =
                        choiceSet.free &&
                        (!choiceSet.max || !choiceSet.free || choiceSet.max > choiceSet.free);

                      if (hasFreeSuffix) {
                        return (
                          ' ' + t('choiceSet.quantityHint.freeSuffix', { free: choiceSet.free })
                        );
                      }

                      return '';
                    };

                    const quantityWarning =
                      getQuantityHint(choiceSet, t) + getFreeSuffix(choiceSet, t);

                    if (choiceSet.choices && choiceSet.choices.length === 0) {
                      return null;
                    }

                    return (
                      <div
                        style={styles.normalChoiceSet}
                        key={`${choiceSet.id}_${index.toString()}`}
                      >
                        {choiceSet.displayType !== 'checkbox' &&
                          choiceSet.displayType !== 'makeameal' && (
                            <div>
                              <NestedChoiceSetBanner
                                key={`${choiceSet.id}_banner`}
                                choiceSet={choiceSet}
                                collapsed={collapsed}
                                toggleCollapsed={toggleCollapsed}
                                hideCaret={mixinRegex.test(choiceSet.name)}
                              />
                              {!!quantityWarning && !collapsed && !isRadioButton && (
                                <Text
                                  themeKey="choiceSetQuantityHint"
                                  style={{
                                    paddingLeft: 20,
                                    fontSize: isMobileDevice ? 12 : 14,
                                  }}
                                  value={quantityWarning}
                                />
                              )}
                            </div>
                          )}
                        {(choiceSet.displayType === 'checkbox' || !collapsed) &&
                          choiceSet.displayType !== 'makeameal' && (
                            <ChoiceSet
                              key={`${choiceSet.id}_display`}
                              choiceSet={choiceSet}
                              adjustOpenPurchaseChoice={targetChoiceSet => {
                                setModified(true);
                                dispatch(adjustOpenPurchaseChoice(targetChoiceSet));
                              }}
                              hideNegativeIngredients={hideNegativeIngredients}
                            />
                          )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                // nested items
                <NestedChoiceSets
                  rootItem={item}
                  choiceSets={choiceSets}
                  adjustOpenPurchaseChoice={targetChoiceSet => {
                    setModified(true);
                    return dispatch(adjustOpenPurchaseChoice(targetChoiceSet));
                  }}
                  hideNegativeIngredients={hideNegativeIngredients}
                  setImage={setImageShowing}
                  onModify={() => setModified(true)}
                  onClick={() => {
                    setScrollTop(psbContainerRef.current.scrollTop);
                  }}
                />
              )}
              {showIndicator && (
                <TouchableOpacity
                  style={combineStyles(
                    styles.indicator,
                    p('scrollForMoreIndicator', CONTAINER_PROPERTIES),
                  )}
                  onClick={() => psbContainerRef.current!.scrollBy({ top: 200 })}
                  ariaLabel="scroll to see more"
                >
                  <RiArrowDownSLine style={p('scrollForMoreIndicator', ['color'])} />
                </TouchableOpacity>
              )}
            </div>
          </div>

          {(isRootItem || addToItemEnabled) && (
            <div
              style={combineStyles(
                p('purchaseEditorBottomSection', ['backgroundColor']),
                isMobileDevice && p('purchaseEditorBottomSectionMobileView', ['backgroundColor']),
                styles.bottomSection,
                isMobileDevice && styles.mobileBottomSection,
                isMobileDevice && { flexDirection: 'column' },
                p('defaultBorder', ['borderTop']),
              )}
            >
              {isRootItem && (
                <>
                  <div
                    style={
                      isMobileDevice ? styles.bottomSectionMobile : styles.bottomSectionDesktop
                    }
                  >
                    <QuantityControl
                      quantity={quantity || 0}
                      maxQuantity={minBalanceStaged}
                      increaseQty={handleIncreaseQty}
                      decreaseQty={() => {
                        setModified(true);
                        dispatch(adjustOpenPurchaseQuantity(-1));
                      }}
                      buttonSize={isMobileDevice ? 30 : 35}
                      increaseEnabled={!validateCart() && daypartAvailability}
                      isMobileDevice={isMobileDevice}
                      containerStyle={{ order: isMobileDevice ? 2 : 1, gridArea: 'b' }}
                    />
                    {makeamealChoiceSet && (
                      <div
                        style={{
                          display: 'flex',
                          flex: 2,
                          order: isMobileDevice ? 1 : 2,
                          gridArea: 'a',
                        }}
                      >
                        <MiamSection
                          openPurchase={openPurchase}
                          makeamealChoiceSet={makeamealChoiceSet}
                        />
                      </div>
                    )}
                    <div style={{ display: 'flex', flex: 2, order: 3, gridArea: 'c' }}>
                      <AddToCartButton
                        onClick={
                          morePagesExist ? () => setActivatePage(p => p + 1) : handleAddToCart
                        }
                        label={getButtonLabel()}
                        value={getButtonValue()}
                        valid={!!((nested ? !nestedItemInValid : valid) && daypartAvailability)}
                        showLabelOnly={morePagesExist}
                      />
                    </div>
                  </div>
                </>
              )}
              {addToItemEnabled && (
                <>
                  {makeamealChoiceSet && (
                    <MiamSection
                      openPurchase={openPurchase}
                      makeamealChoiceSet={makeamealChoiceSet}
                      stages={stages}
                    />
                  )}
                  <StandardButton
                    themeKey="modalProceedButton"
                    label={`${t('button.addToItem')}`}
                    onClick={handleAddToItem}
                    style={combineStyles(styles.button)}
                    disabled={
                      !isSelectionValid ||
                      (minBalanceStaged !== undefined && (quantity || 0) > minBalanceStaged) ||
                      minBalanceStaged === 0 ||
                      !daypartAvailability
                    }
                    disabledStyle={styles.disabledButton}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </Modal>
      {messageBox && (
        <MessageModal
          title="This item has not been added to the cart"
          message="Are you sure you want to continue to the menu?"
          titleStyle={{ fontSize: 20 }}
          desktopMaxWidth={450}
          desktopWidth={450}
          handleClose={() => {
            setMessageBox(false);
            dispatch(clearOpenPurchase());
          }}
        />
      )}
    </>
  );
};

const styles: Styles = {
  mobileContainer: {
    display: 'flex',
    flexDirection: 'column',
    //minHeight: '100%',
    // maxHeight: '100%',

    overflowY: 'auto',
    // For paralax image header
    borderRadius: 0,
    padding: 0,
    gap: 0,
  },
  modalContainer: {
    flex: 1,
    // auto sized grid so that the component works correctly if the image is missing
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'minmax(0, 1fr)',

    overflow: 'hidden',
    fontSize: 10,
    borderRadius: 0,
    padding: 0,
    gap: 0,
  },
  productImage: {
    position: 'relative',
    width: '100%',
    maxHeight: '50%', // TODO fix this?

    // Commenting out z-perspective parallaxing due to iOS/safari buggy UI breaks

    // transformStyle: 'preserve-3d',
    // WebkitTransformStyle: 'preserve-3d',
    // transform: `translateZ(-2px) scale(2)${
    //   // this is for the users who are viewing on a touchscreen-less display, as the scroll shifts this.
    //   // window.matchMedia('(pointer: fine)').matches ? ' translateX(-4px)' : ''
    //   ''
    // }
    // `,

    transformOrigin: 'top',
    zIndex: -1,
  },
  scrollable: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    position: 'relative',
    overflowY: 'auto',
  },

  scrollableExtraPadding: {
    paddingBottom: BOTTOM_PADDING,
  },
  price: {
    fontSize: 16,
  },

  button: {
    borderRightWidth: 0,
    borderLeftWidth: 0,
    borderBottomWidth: 0,
    flexGrow: 1,
    padding: 10,
  },

  indicator: {
    position: 'sticky',
    left: 20,
    bottom: 10,
    height: 50,
    width: 50,
    backgroundColor: 'green',
    zIndex: 900,
    borderRadius: '50%',

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 3,
    fontSize: 40,
  },
  bottomSection: {
    display: 'flex',
    flexDirection: 'row',
    gap: 30,
    padding: '25px 30px',
    justifyContent: 'center',
  },
  mobileBottomSection: {
    padding: 30,
    paddingTop: 20,
  },
  container: {
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    gap: 20,
  },
  normalChoiceSet: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 15,
  },
  backButton: {
    position: 'absolute',
    left: 0,
    top: 0,
    padding: '1em',
    zIndex: 900,
  },
  bottomSectionMobile: {
    display: 'grid',
    gap: 15,
    gridTemplateAreas: `"a a a"
     "b c c"`,
  },
  bottomSectionDesktop: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    gap: 15,
    order: 11,
  },
};

export default PurchaseEditorModal;
